// Central Studio navigation config.
//
// Single source of truth for the sidebar: ordered groups of items, each
// with allowed-roles, thesis-stage, data-sensitivity, status badge, and
// either a viewId (dispatches via window.IFI.studio.<modKey>.init()) or
// an externalUrl (renders as link-out).
//
// `main.ts` derives its VIEWS lookup + role-filtering + sidebar render
// from these constants — no menu logic scattered elsewhere.

import type { Role } from './db/types';

export type DataSensitivity = 'public' | 'internal' | 'sensitive' | 'restricted';

export type ThesisStage =
  | 'orientation'
  | 'data_collection'
  | 'validation'
  | 'weighting'
  | 'scoring'
  | 'diagnosis'
  | 'reporting'
  | 'administration';

export type ItemStatus = 'live' | 'lokal' | 'soon' | 'external';

export interface NavGroup {
  id: ThesisStage;
  order: number;
  label: string;     // Indonesian (primary)
  labelEn?: string;  // English overlay (i18n)
  helper: string;
  helperEn?: string;
}

export interface NavItem {
  id: string;             // sidebar key (URL-safe, matches viewId where dispatching)
  viewId?: string;        // when set, click dispatches to modKey
  modKey?: string;        // window.IFI.studio.<modKey> init() target
  externalUrl?: string;   // when set, render as anchor link-out
  group: ThesisStage;
  label: string;          // Indonesian (primary)
  labelEn?: string;       // English overlay (i18n)
  helper: string;
  helperEn?: string;
  icon: string;
  allowedRoles: Role[];
  dataSensitivity: DataSensitivity;
  status: ItemStatus;
  order: number;
  title?: string;
}

// ===== Groups (ordered) =====

export const NAV_GROUPS: NavGroup[] = [
  { id: 'orientation',     order: 1, label: 'Orientasi Penelitian',       labelEn: 'Research Orientation',     helper: 'Memahami tujuan, alur, dan keluaran INFRAIMPACT.',                helperEn: 'Understand the goals, flow, and outputs of INFRAIMPACT.' },
  { id: 'data_collection', order: 2, label: 'Pengambilan Data',           labelEn: 'Data Collection',          helper: 'Tempat ahli, narasumber, dan tim lapangan mengisi data.',          helperEn: 'Where experts, respondents, and field teams enter data.' },
  { id: 'validation',      order: 3, label: 'Validasi dan Kurasi Data',   labelEn: 'Validation & Data Curation', helper: 'Memastikan data layak sebelum penilaian.',                       helperEn: 'Ensure data is fit for purpose before scoring.' },
  { id: 'weighting',       order: 4, label: 'Pembobotan Expert Judgment', labelEn: 'Expert Judgment Weighting', helper: 'BWM: bobot indikator dari panel ahli.',                            helperEn: 'BWM: indicator weights derived from the expert panel.' },
  { id: 'scoring',         order: 5, label: 'Perhitungan INFRAIMPACT',    labelEn: 'INFRAIMPACT Computation',  helper: 'Indikator → skor → S_CAUSE / S_IMPACT → kuadran.',                 helperEn: 'Indicator → score → S_CAUSE / S_IMPACT → quadrant.' },
  { id: 'diagnosis',       order: 6, label: 'Diagnosis dan Hasil',        labelEn: 'Diagnosis & Results',      helper: 'Hasil diagnosis aset dan implikasi.',                              helperEn: 'Asset-level diagnosis output and implications.' },
  { id: 'reporting',       order: 7, label: 'Ekspor dan Pelaporan',       labelEn: 'Export & Reporting',       helper: 'Output untuk tesis dan laporan eksekutif.',                        helperEn: 'Outputs for the thesis and executive briefs.' },
  { id: 'administration',  order: 8, label: 'Administrasi Sistem',        labelEn: 'System Administration',    helper: 'Pengguna, konfigurasi, dan jejak audit.',                          helperEn: 'Users, configuration, and audit trail.' },
];

// ===== Items (ordered within each group) =====

const ALL_ROLES: Role[] = ['super_admin', 'project_admin', 'source_person', 'viewer'];
const ADMIN_ROLES: Role[] = ['super_admin', 'project_admin'];
const RESEARCH_ROLES: Role[] = ['super_admin', 'project_admin', 'source_person'];
const SUPER_ONLY: Role[] = ['super_admin'];

export const NAV_ITEMS: NavItem[] = [
  // ===== G1 ORIENTASI =====
  {
    id: 'panduan', viewId: 'panduan', modKey: 'panduan',
    group: 'orientation', order: 0,
    label: 'Panduan / Tur Singkat', labelEn: 'Guide / Quick Tour',
    helper: 'Cara membaca dasbor ini — untuk pengunjung baru, pembimbing, dan penguji.',
    helperEn: 'How to read this dashboard — for first-time visitors, supervisors, and examiners.',
    icon: '🧭', allowedRoles: ALL_ROLES, dataSensitivity: 'public', status: 'live',
  },
  {
    id: 'research-overview', viewId: 'research-overview', modKey: 'researchOverview',
    group: 'orientation', order: 1,
    label: 'Ringkasan Penelitian', helper: 'Tujuan, pertanyaan riset, alur QUAL→QUAN, unit analisis.',
    icon: '📋', allowedRoles: ALL_ROLES, dataSensitivity: 'public', status: 'live',
  },
  {
    id: 'research-workflow-tracker', viewId: 'research-workflow-tracker', modKey: 'researchWorkflowTracker',
    group: 'orientation', order: 2,
    label: 'Alur Penelitian', helper: '10 tahap tesis dari planning ke defense + status per tahap.',
    icon: '📈', allowedRoles: RESEARCH_ROLES, dataSensitivity: 'internal', status: 'live',
  },
  {
    id: 'methodology-explainer', viewId: 'methodology-explainer', modKey: 'methodologyExplainer',
    group: 'orientation', order: 3,
    label: 'Metodologi dan Rumus', helper: 'Penjelasan mixed-methods, BWM, CAUSE/IMPACT decoupling, matriks 2×2.',
    icon: '📐', allowedRoles: ALL_ROLES, dataSensitivity: 'public', status: 'live',
  },
  {
    id: 'literature-review', viewId: 'literature-review', modKey: 'literatureReview',
    group: 'orientation', order: 4,
    label: 'Literature Review',
    helper: 'Rujukan tesis yang benar-benar dipakai sebagai dasar INFRAIMPACT.',
    icon: '📚', allowedRoles: ALL_ROLES, dataSensitivity: 'public', status: 'live',
  },

  // ===== G2 PENGAMBILAN DATA =====
  {
    id: 'poc-locations', viewId: 'poc-locations', modKey: 'pocLocations',
    group: 'data_collection', order: 0,
    label: 'Pemilihan Lokasi PoC & Fieldwork', helper: 'Fase 1 (awal): pilih 3 kasus ekstrem + rencana luring (FGD, observasi, telaah dokumen Dit. Sanitasi/SIINSAN).',
    icon: '📍', allowedRoles: RESEARCH_ROLES, dataSensitivity: 'internal', status: 'live',
  },
  {
    id: 'data-collection-hub', viewId: 'data-collection-hub', modKey: 'dataCollectionHub',
    group: 'data_collection', order: 0.5,
    label: 'Pusat Pengisian Data', helper: 'Landing tugas pengisian untuk source_person + tim peneliti.',
    icon: '📥', allowedRoles: RESEARCH_ROLES, dataSensitivity: 'internal', status: 'live',
  },
  {
    id: 'raw-viewer', viewId: 'raw-viewer', modKey: 'rawAssetDatabase',
    group: 'data_collection', order: 1,
    label: 'Raw Asset Database', helper: 'Tambah/edit aset langsung di tabel (gaya Excel). Hapus = masuk Bin 30 hari.',
    icon: '🗄', allowedRoles: ALL_ROLES, dataSensitivity: 'internal', status: 'live',
  },
  {
    id: 'expert-judgment-bwm', viewId: 'bwm-studio', modKey: 'bwmStudio',
    group: 'data_collection', order: 2,
    label: 'Expert Judgment BWM', helper: 'Pengisian preferensi ahli (Best-Worst) — entry point khusus narasumber.',
    icon: '🎯', allowedRoles: RESEARCH_ROLES, dataSensitivity: 'sensitive', status: 'live',
    title: 'Expert Judgment BWM (input wizard, same view as Studio BWM)',
  },
  {
    id: 'interview-form', viewId: 'interview-form', modKey: 'interviewForm',
    group: 'data_collection', order: 3,
    label: 'Wawancara Narasumber', helper: 'Panduan dan pencatatan wawancara semi-terstruktur.',
    icon: '🎙', allowedRoles: RESEARCH_ROLES, dataSensitivity: 'sensitive', status: 'live',
  },
  {
    id: 'fgd-form', viewId: 'fgd-form', modKey: 'fgdForm',
    group: 'data_collection', order: 4,
    label: 'Diskusi Kelompok Terarah (FGD)', helper: 'Panduan dan ringkasan FGD untuk validasi temuan.',
    icon: '👥', allowedRoles: RESEARCH_ROLES, dataSensitivity: 'sensitive', status: 'live',
  },
  {
    id: 'field-form', viewId: 'field-form', modKey: 'fieldForm',
    group: 'data_collection', order: 5,
    label: 'Observasi Lapangan', helper: 'Form kondisi aset, fungsi layanan, dan bukti operasional.',
    icon: '🔍', allowedRoles: RESEARCH_ROLES, dataSensitivity: 'sensitive', status: 'live',
  },
  {
    id: 'doc-form', viewId: 'doc-form', modKey: 'docForm',
    group: 'data_collection', order: 6,
    label: 'Telaah Dokumen', helper: 'Daftar bukti dokumen (DIPA, kontrak, BAST, regulasi, dll).',
    icon: '📄', allowedRoles: RESEARCH_ROLES, dataSensitivity: 'sensitive', status: 'live',
  },

  // ===== G3 VALIDASI DAN KURASI =====
  {
    id: 'raw-data-manager', viewId: 'raw-data-manager', modKey: 'rawDataManager',
    group: 'validation', order: 1,
    label: 'Manajer Data Mentah', helper: 'Kurasi raw_asset_data sebelum masuk perhitungan indikator.',
    icon: '🗂', allowedRoles: RESEARCH_ROLES, dataSensitivity: 'internal', status: 'live',
  },
  {
    id: 'data-validation', viewId: 'raw-data-manager', modKey: 'rawDataManager',
    group: 'validation', order: 1.5,
    label: 'Validasi Data', helper: 'Jalankan state machine validasi: field_confirmed / corrected / rejected / returned_for_revision (sama dengan Manajer Data Mentah).',
    icon: '🔁', allowedRoles: ADMIN_ROLES, dataSensitivity: 'internal', status: 'live',
    title: 'Alias dari Manajer Data Mentah — fokus pada validation state machine.',
  },
  {
    id: 'cvi-aiken-panel', viewId: 'cvi-aiken-panel', modKey: 'cviAikenPanel',
    group: 'validation', order: 2,
    label: 'Panel Validitas (CVI/Aiken)', helper: 'Skor relevansi 1-4 per rater × indikator; auto-hitung V + I-CVI.',
    icon: '✅', allowedRoles: ADMIN_ROLES, dataSensitivity: 'internal', status: 'live',
  },
  {
    id: 'thematic-coding', viewId: 'thematic-coding', modKey: 'thematicCoding',
    group: 'validation', order: 3,
    label: 'Koding Tematik', helper: 'Codebook + quote-level coding (institutional void, fiscal mismatch, dll).',
    icon: '🏷', allowedRoles: RESEARCH_ROLES, dataSensitivity: 'sensitive', status: 'live',
  },
  {
    id: 'triangulation-matrix', viewId: 'triangulation-matrix', modKey: 'triangulationMatrix',
    group: 'validation', order: 4,
    label: 'Matriks Triangulasi', helper: 'Auto-aggregator cause_tags + impact_tags dari interview/FGD/dokumen.',
    icon: '△', allowedRoles: RESEARCH_ROLES, dataSensitivity: 'sensitive', status: 'live',
  },
  {
    id: 'quality', viewId: 'quality', modKey: 'quality',
    group: 'validation', order: 5,
    label: 'Kualitas Data', helper: 'Coverage assets + coding progress + score coverage + recent audit log.',
    icon: '📊', allowedRoles: ADMIN_ROLES, dataSensitivity: 'internal', status: 'live',
  },

  // ===== G4 PEMBOBOTAN =====
  {
    id: 'bwm-studio', viewId: 'bwm-studio', modKey: 'bwmStudio',
    group: 'weighting', order: 1,
    label: 'Studio BWM', helper: '9-step Rezaei wizard + agregasi panel via geometric mean.',
    icon: '⚖', allowedRoles: ADMIN_ROLES, dataSensitivity: 'sensitive', status: 'live',
  },
  {
    id: 'bwm-results-review', viewId: 'bwm-studio', modKey: 'bwmStudio',
    group: 'weighting', order: 2,
    label: 'Review Bobot', helper: 'Buka Studio BWM untuk meninjau bobot tersimpan + consistency ratio per panel (gunakan tombol "Bobot tersimpan").',
    icon: '🔎', allowedRoles: ADMIN_ROLES, dataSensitivity: 'sensitive', status: 'live',
    title: 'Alias dari Studio BWM — pintu masuk ke review bobot tersimpan.',
  },

  // ===== G5 PERHITUNGAN =====
  {
    id: 'indicator-registry', viewId: 'indicator-registry', modKey: 'indicatorRegistry',
    group: 'scoring', order: 1,
    label: 'Registry Indikator', helper: 'Definisi operasional, formula, threshold, lifecycle indikator.',
    icon: '📚', allowedRoles: ALL_ROLES, dataSensitivity: 'public', status: 'live',
  },
  {
    id: 'calc-studio', viewId: 'calc-studio', modKey: 'calcStudio',
    group: 'scoring', order: 2,
    label: 'Perhitungan INFRAIMPACT', helper: '8-step trace: raw → normalize → weight → S_CAUSE/S_IMPACT → quadrant.',
    icon: '🧮', allowedRoles: ADMIN_ROLES, dataSensitivity: 'sensitive', status: 'live',
  },

  // ===== G6 DIAGNOSIS =====
  {
    id: 'diagnostic-dashboard', externalUrl: '/',
    group: 'diagnosis', order: 1,
    label: 'Dashboard Diagnosis', helper: 'Buka dashboard publik (matriks 2×2, peta, tren, ranking) di tab baru.',
    icon: '📊', allowedRoles: ALL_ROLES, dataSensitivity: 'public', status: 'external',
  },
  {
    id: 'asset-case-review', viewId: 'asset-case-review', modKey: 'assetCaseReview',
    group: 'diagnosis', order: 2,
    label: 'Telaah Kasus Aset', helper: 'Proof-of-concept n=3 (optimal/median/severe) — tes diferensiasi kerangka.',
    icon: '🎯', allowedRoles: RESEARCH_ROLES, dataSensitivity: 'internal', status: 'live',
  },
  {
    id: 'policy-insight', viewId: 'policy-insight', modKey: 'policyInsight',
    group: 'diagnosis', order: 3,
    label: 'Insight Kebijakan', helper: 'Implikasi manajerial &amp; kebijakan — untuk pengambil keputusan tingkat tinggi.',
    icon: '💡', allowedRoles: ALL_ROLES, dataSensitivity: 'public', status: 'live',
  },

  // ===== G7 EKSPOR =====
  {
    id: 'export-center', viewId: 'export-center', modKey: 'exportCenter',
    group: 'reporting', order: 1,
    label: 'Ekspor Data', helper: 'CSV download per tabel core + backup JSON snapshot.',
    icon: '📤', allowedRoles: ADMIN_ROLES, dataSensitivity: 'internal', status: 'live',
  },
  {
    id: 'thesis-tables', viewId: 'export-center', modKey: 'exportCenter',
    group: 'reporting', order: 2,
    label: 'Tabel untuk Tesis', helper: 'Buka Export Center untuk download CSV per tabel — siap diolah jadi tabel lampiran tesis.',
    icon: '📑', allowedRoles: ADMIN_ROLES, dataSensitivity: 'internal', status: 'live',
    title: 'Alias dari Ekspor Data — fokus framing untuk thesis writing.',
  },

  // ===== G8 ADMINISTRASI =====
  {
    id: 'admin-users', viewId: 'admin-users', modKey: 'adminUsers',
    group: 'administration', order: 1,
    label: 'Manajemen Pengguna', helper: 'Invite, ubah peran, aktif/nonaktif. Super_admin only.',
    icon: '👤', allowedRoles: SUPER_ONLY, dataSensitivity: 'restricted', status: 'live',
  },
  {
    id: 'assignments-admin', viewId: 'assignments-admin', modKey: 'assignmentsAdmin',
    group: 'administration', order: 1.5,
    label: 'Manajemen Tugas', helper: 'Buat + kelola tugas pengumpulan data per source_person (RLS-gated).',
    icon: '📋', allowedRoles: SUPER_ONLY, dataSensitivity: 'sensitive', status: 'live',
  },
  {
    id: 'settings', viewId: 'settings', modKey: 'settings',
    group: 'administration', order: 2,
    label: 'Pengaturan Sistem', helper: 'Info session, metode default, count tabel, link dokumentasi. Memuat konfigurasi sensitif.',
    icon: '⚙', allowedRoles: SUPER_ONLY, dataSensitivity: 'restricted', status: 'live',
  },
  {
    id: 'audit-log-viewer', viewId: 'audit-log-viewer', modKey: 'auditLogViewer',
    group: 'administration', order: 3,
    label: 'Log Audit', helper: 'audit_logs (event log) + data_change_audit (SHA256 hash chain).',
    icon: '📜', allowedRoles: SUPER_ONLY, dataSensitivity: 'restricted', status: 'live',
  },
];

// ===== Derived helpers =====

export function navTreeForRole(role: Role): Array<{ group: NavGroup; items: NavItem[] }> {
  const groups = [...NAV_GROUPS].sort((a, b) => a.order - b.order);
  return groups
    .map((group) => ({
      group,
      items: NAV_ITEMS
        .filter((it) => it.group === group.id && it.allowedRoles.includes(role))
        .sort((a, b) => a.order - b.order),
    }))
    .filter((entry) => entry.items.length > 0);
}

/** Look up a single item by sidebar id (the router key). */
export function findNavItem(id: string): NavItem | undefined {
  return NAV_ITEMS.find((it) => it.id === id);
}

/** Returns the modKey or undefined for an external-link item. */
export function modKeyOf(item: NavItem): string | undefined {
  return item.modKey;
}

/** Build the {viewId: allowedRoles[]} map used by legacy applyNavVisibility. */
export function roleMatrix(): Record<string, Role[]> {
  const m: Record<string, Role[]> = {};
  for (const it of NAV_ITEMS) {
    if (!it.viewId) continue;
    // Aliases (multiple items pointing to same viewId) union their roles.
    const prev = m[it.viewId] ?? [];
    const merged = Array.from(new Set([...prev, ...it.allowedRoles])) as Role[];
    m[it.viewId] = merged;
  }
  return m;
}
